import * as React from 'react';
import { uiMarkerInternal } from '../recipes/shared';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { buttonRecipe, type ButtonRecipeProps } from '../recipes/button.recipe';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    ButtonRecipeProps {
  /**
   * Render the child element instead of a <button>, keeping the styling. Use
   * for links that should look like buttons:
   * `<Button asChild><Link to="/x">Go</Link></Button>`
   */
  asChild?: boolean;
  /**
   * Shows a spinner and blocks interaction. Prefer this over disabling the
   * button yourself — it keeps the width stable and announces busy state.
   */
  loading?: boolean;
  /** Icon shown before the label. Sized automatically. */
  leadingIcon?: React.ReactNode;
  /** Icon shown after the label. */
  trailingIcon?: React.ReactNode;
  /**
   * Render an `<a>` to this address, styled as the button.
   *
   *   <Button href={siteUrl} external>Open your site</Button>
   *
   * The product had ten of these as `<Button asChild><a href target rel>`,
   * which was the library prescribing a raw anchor. `asChild` is still the
   * way to wrap a router `<Link>`; `href` is for a plain address, and gets
   * the icon and loading slots that `asChild` cannot offer.
   */
  href?: string;
  /** Opens in a new tab, with the rel that makes that safe. Needs `href`. */
  external?: boolean;
  /** The `download` attribute, for an `href` that should save rather than open. */
  download?: string | boolean;
}

/**
 * The only button in the app.
 *
 * Handles the parts that are easy to get wrong by hand: the busy state, icon
 * sizing, the focus ring, and keeping the height aligned with form controls
 * next to it.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      grow,
      fullWidth,
      pill,
      reveal,
      asChild = false,
      loading = false,
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      type,
      href,
      external = false,
      download,
      ...props
    },
    ref
  ) {
    const Comp = asChild ? Slot : 'button';

    // asChild hands rendering to the child, so it can only receive a single
    // element — the spinner and icon slots would break the Slot contract.
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          leadingIcon
        )}
        {children}
        {!loading && trailingIcon}
      </>
    );

    if (
      import.meta.env.DEV &&
      typeof size === 'string' &&
      size.startsWith('icon') &&
      !props['aria-label'] &&
      !props['aria-labelledby']
    ) {
      console.warn(
        '[library/Button] An icon-only button needs an aria-label so screen ' +
          'readers can announce what it does.'
      );
    }

    const classes = cn(
      buttonRecipe({ variant, size, grow, fullWidth, pill, reveal }),
      className
    );

    if (!asChild && href !== undefined) {
      /*
       * An anchor has no `disabled`; a disabled link drops its href and says
       * so, which is the closest the platform offers.
       */
      const inert = disabled || loading;
      const anchorRef = ref as unknown as React.Ref<HTMLAnchorElement>;
      const anchorProps =
        props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a
          ref={anchorRef}
          href={inert ? undefined : href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          download={download}
          aria-disabled={inert || undefined}
          aria-busy={loading || undefined}
          className={classes}
          {...anchorProps}
          // After the spread, so a handler passed in cannot run while inert.
          onClick={inert ? undefined : anchorProps.onClick}
        >
          {content}
        </a>
      );
    }

    return (
      <Comp
        ref={ref}
        // Buttons inside a <form> submit by default, which has caused
        // accidental submits from row-action buttons. Default to "button".
        type={asChild ? undefined : (type ?? 'button')}
        className={classes}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);
